// CUSTODY OPERATIONS' SMALL PIECES (UI rebuild batch 4, the custody hand-off — docs/ui-rebuild/handoff/CUSTODY.md).
// The section's tabs, the GATED ADDRESS (head only: never the ending the typing gate asks for — verification 5.3), the
// approval window as one line, the pipeline's stage pill, and the words the screens share. Every word is the wallet
// lane's own (lib/custody-format.ts CLASS_WORD / POOL_WORD / KIND_WORD; contracts/wallet.mjs PAYOUT_STAGE_WORDS; the
// deposit STATUS words of screens/custody/Deposits.tsx). No rule is decided here.
import { SigninQuiet } from './SigninLine';
import { isCustodySignin } from '../../../lib/errors';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { custody as api, type ApprovalRow, type DepositRow, type WalletBalance } from '../../../lib/api';
import { CLASS_WORD, POOL_WORD, KIND_WORD } from '../../../lib/custody-format';
import { displayAsset, fmtAmount, fmtTime } from '../../../lib/format';
import { windowOpenUntil } from '../../../lib/custody-screen';
import { PAYOUT_STAGE_WORDS } from '../../../../../contracts/wallet.mjs';
import { Tabs, StateLine, PageHead } from '../kit';
import { Pill, type PillTone } from '../../Pill';
import { windowRefusal } from '../../../screens/custody/refusals';

/** THE SECTION'S FOUR SCREENS (his yes to the redesign, 2 Nov 2026: four screens instead of seven) — Waiting on you ·
 *  Deposits & addresses · Wallets · Settings; short words on the phone so the row fits one line (DESIGN-SYSTEM §10.9).
 *  `waiting`: the count of what needs him, once a read has said it. */
export type CustodyView = 'waiting' | 'deposits' | 'wallets' | 'settings';
export function CustodyTabs({ current, waiting }: { current: CustodyView | null; waiting?: number | null }) {
  return (
    <Tabs
      label="Custody"
      current={current ?? ''}
      items={[
        { id: 'waiting', label: 'Waiting on you', short: 'Waiting', to: '/custody', count: waiting ?? null },
        { id: 'deposits', label: 'Deposits & addresses', short: 'Deposits', to: '/custody/deposits' },
        { id: 'wallets', label: 'Wallets', to: '/custody/wallets' },
        { id: 'settings', label: 'Settings', to: '/custody/settings' },
      ]}
    />
  );
}

/** The section's one head: "Custody", how it signs, and the screen's own actions at the right (icon buttons on the phone are
 *  the caller's choice). */
export function CustodyHead({ actions }: { actions?: ReactNode }) {
  return <PageHead title="Custody" sub="Ledger by cable · TRC20" inline actions={actions} />;
}

/** A section inside a custody screen: its title, and its one action at the right. */
export function SectionTitle({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="mb-3 mt-6 flex min-h-11 items-center justify-between gap-3 lg:min-h-10">
      <h2 className="text-title font-semibold">{title}</h2>
      {action}
    </div>
  );
}

/** A custody figure: the amount by rule 10, then its unit muted (DESIGN-SYSTEM §10.3) — "412,330 USDT·TRC". null is "Not
 *  known", never 0. `plain` keeps the parent's colour for the unit too (inside a sentence of hint text). */
export function Figure({ value, asset, plain = false }: { value: string | null | undefined; asset: string; plain?: boolean }) {
  if (value === null || value === undefined) return <span className="text-tg-hint">Not known</span>;
  return (
    <span className="whitespace-nowrap">
      <span className="font-mono tabular">{fmtAmount(value, asset)}</span>{' '}
      <span className={plain ? '' : 'font-sans text-meta font-normal text-tg-hint'}>{displayAsset(asset)}</span>
    </span>
  );
}

/** THE SCREEN'S ACTIONS, at the right of the head (DESIGN-SYSTEM §10.10): buttons from lg; on the phone one "+" that opens
 *  them, so they never take a row of their own. The first is the screen's primary when `primary`. */
export function CustodyActions({ items, primary = false }: { items: ReadonlyArray<{ label: string; to: string }>; primary?: boolean }) {
  const [open, setOpen] = useState(false);
  if (items.length === 0) return null;
  return (
    <>
      <span className="flex gap-2 max-lg:hidden">
        {items.map((it, i) => <Link key={it.to} to={it.to} className={`${primary && i === 0 ? 'btn-primary' : 'btn-secondary'} whitespace-nowrap px-4 text-body`}>{it.label}</Link>)}
      </span>
      <span className="relative lg:hidden">
        <button type="button" aria-label="Actions" aria-expanded={open} onClick={() => setOpen((v) => !v)}
          className="btn-secondary inline-flex h-11 w-11 items-center justify-center p-0"><Plus className="h-5 w-5" aria-hidden /></button>
        {open && (
          <span role="menu" className="absolute right-0 top-12 z-30 flex min-w-[12rem] flex-col rounded-[10px] border border-stroke bg-panel p-1 shadow-lg">
            {items.map((it) => <Link key={it.to} to={it.to} role="menuitem" className="flex min-h-11 items-center rounded-lg px-3 text-body hover:bg-panel-2" onClick={() => setOpen(false)}>{it.label}</Link>)}
          </span>
        )}
      </span>
    </>
  );
}

/**
 * THE GATED ADDRESS — an address on an approval, pay or sign surface: its head only, and the words "ending withheld".
 * The kit's Address shows the last four, four of the six characters the typing gate asks him for (verification 5.3);
 * this never draws a tail, and offers no copy.
 */
export function GatedAddress({ value, head = 10 }: { value: string; head?: number }) {
  return (
    <span className="inline-flex min-w-0 flex-wrap items-baseline justify-end gap-x-2">
      <span className="font-mono text-body">{value.slice(0, head)}…</span>
      <span className="text-meta text-tg-hint">ending withheld</span>
    </span>
  );
}

/** A card's kind, in the signing page's word ("Payout", "Sweep", "Refill" …). */
/** A card's kind in the desk's words; one it does not know is said in fixed words, never as the server's own text (Astra,
 *  max, pre-live, 29 Oct 2026: beside the "type the ending" box a crafted kind could hand the operator the answer). */
// A release (N-099) moves a Merchant Hot deposit to Hot; its word until the wallet's KIND_WORD names it.
export const kindWord = (kind: string): string => KIND_WORD[kind] ?? (kind === 'release' ? 'Release' : 'Card to sign');

/**
 * Where a card's money leaves, as the register says it: the wallet's class when the balances read names the address;
 * else the pool's word; else null. Display only — the signer reads the source from the bytes.
 */
export function sourceWord(a: Pick<ApprovalRow, 'fromAddress' | 'pool' | 'kind'>, wallets: readonly WalletBalance[] | undefined): string | null {
  const w = wallets?.find((x) => x.address === a.fromAddress);
  if (w) return CLASS_WORD[w.class] ?? w.class;
  if (a.kind === 'sweep') return CLASS_WORD['deposit'] ?? 'Deposit';
  if (a.kind === 'refill') return CLASS_WORD['treasury'] ?? 'Treasury';
  if (a.pool) return POOL_WORD[a.pool] ?? a.pool;
  return null;
}

/** A payout address's stage, in the wallet's words (db/0098: Approved → Test sent → Whitelisted). */
export const stageWord = (stage: string | null | undefined): string | null =>
  stage ? ((PAYOUT_STAGE_WORDS as Record<string, string>)[stage] ?? stage) : null;

/** The pipeline's stages (screens/custody/Deposits.tsx STATUS) — never "Ready to sweep" (verification 2.6). */
export const DEPOSIT_STAGE: Record<DepositRow['status'], { label: string; tone: PillTone }> = {
  detected: { label: 'Detected', tone: 'neutral' },
  confirmed: { label: 'Confirmed', tone: 'neutral' },
  screening: { label: 'Screening', tone: 'neutral' },
  held: { label: 'Held', tone: 'warn' },
  routed: { label: 'Routed', tone: 'good' },
  swept: { label: 'Swept', tone: 'good' },
  quarantined: { label: 'Flagged', tone: 'bad' },
  rejected: { label: 'Rejected', tone: 'bad' },
};
/** `calm`: KYT says other than clean (deposit-words kytNotClean) — a stage that would be green is drawn neutral. */
export function StagePill({ status, calm = false }: { status: DepositRow['status']; calm?: boolean }) {
  const s = DEPOSIT_STAGE[status] ?? { label: status, tone: 'neutral' as PillTone };
  return <Pill tone={calm && s.tone === 'good' ? 'neutral' : s.tone}>{s.label}</Pill>;
}

/** A clock time on the desk's clock. */
export const clock = (iso: string | null | undefined): string => (iso ? fmtTime(iso) : '');

/** The window's spans, in minutes (screens/custody/MoveSheet.tsx WINDOW_CHOICES). */
const WINDOW_CHOICES = [30, 60, 120, 240] as const;

/**
 * THE APPROVAL WINDOW AS ONE LINE — the desk attended until a time, or not — with the opener behind "Open window"
 * (screens/custody/MoveSheet.tsx WindowControl's read and write, in the lightest form). Every move and every sweep is
 * refused without a window; the line says which it is, and a failed read is said as that, never as "closed".
 */
export function WindowLine({ className = '' }: { className?: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [minutes, setMinutes] = useState<number>(60);
  const q = useQuery({ queryKey: ['custody', 'approval-window'], queryFn: api.approvalWindow, refetchInterval: 30_000 });
  const m = useMutation({
    mutationFn: () => api.openApprovalWindow(minutes),
    onSuccess: () => {
      setOpen(false);
      for (const key of ['approval-window', 'summary', 'approvals', 'deposits']) void qc.invalidateQueries({ queryKey: ['custody', key] });
    },
  });
  const until = q.data ? windowOpenUntil(q.data) : null;
  const opener = (
    <button type="button" className="btn-link tap-44 text-body" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
      {until ? 'Extend' : 'Open window'}
    </button>
  );
  return (
    <div className={className}>
      {q.isError ? (
        isCustodySignin(q.error) ? <SigninQuiet error={q.error} /> : <StateLine tone="bad" more={String((q.error as Error)?.message ?? q.error)}
          action={<button type="button" className="btn-link tap-44 text-body" onClick={() => void q.refetch()}>Try again</button>}>
          Couldn&apos;t read the approval window.
        </StateLine>
      ) : !q.data ? (
        <StateLine>Reading the approval window…</StateLine>
      ) : until ? (
        <StateLine tone="good" action={opener}>Desk attended until <span className="font-mono tabular">{clock(until)}</span></StateLine>
      ) : (
        <StateLine tone="warn" action={opener}>Desk window closed</StateLine>
      )}
      {open && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select className="select w-auto" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} aria-label="Window length">
            {WINDOW_CHOICES.map((n) => <option key={n} value={n}>{n >= 60 ? `${n / 60} h` : `${n} min`}</option>)}
          </select>
          <button type="button" className="btn-secondary px-4 text-body" disabled={m.isPending} onClick={() => m.mutate()}>
            {m.isPending ? 'Opening…' : until ? 'Extend window' : 'Open window'}
          </button>
        </div>
      )}
      {m.isError && <StateLine tone="bad" className="mt-1" more={windowRefusal(m.error)}>Not opened.</StateLine>}
    </div>
  );
}
